import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import { PageHero, SectionHead, Marquee } from "../editorial/Layout";

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const lbl = {
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: 10,
  color: "var(--mute)",
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  marginBottom: 8,
  display: "block",
};
const inp = {
  width: "100%",
  padding: "14px 0",
  background: "transparent",
  border: 0,
  borderBottom: "1px solid var(--ink)",
  fontSize: 16,
  fontFamily: "inherit",
  color: "var(--ink)",
  outline: "none",
};
const sel = {
  ...inp,
  appearance: "none",
  paddingRight: 24,
  backgroundImage:
    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8'><path d='M0 0l6 8 6-8z' fill='%231B1F23'/></svg>\")",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 4px center",
};

const ContactForm = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [proj, setProj] = useState("Kitchen");
  const [budget, setBudget] = useState("40k – 80k");
  const [start, setStart] = useState("Q3 2026");
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [touched, setTouched] = useState(false);

  const emailInvalid = touched && email !== "" && !email.includes("@");
  const canSend = !sending && name && email.includes("@") && msg.length > 4;

  const submit = (e) => {
    e.preventDefault();
    if (!canSend) return;
    setSending(true);
    emailjs
      .send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          fullName: name,
          senderEmail: email,
          phone,
          projectType: proj,
          budget,
          start,
          message: msg,
        },
        { publicKey: EMAILJS_PUBLIC_KEY }
      )
      .then(() => {
        setSent(true);
        setTimeout(() => {
          setName("");
          setEmail("");
          setPhone("");
          setMsg("");
          setSent(false);
          setTouched(false);
        }, 3500);
      })
      .catch((error) => {
        console.error("EmailJS send failed", error);
      })
      .finally(() => setSending(false));
  };

  return (
    <form onSubmit={submit}>
      <style>{`
        .cf-row { display: grid; gap: 32px; margin-bottom: 32px; }
        .cf-r2 { grid-template-columns: 1fr 1fr; }
        .cf-r3 { grid-template-columns: 1fr 1fr 1fr; }
        .cf-field input:focus, .cf-field select:focus, .cf-field textarea:focus { border-bottom-color: var(--clay) !important; }
        .cf-err { font-size: 11.5px; color: var(--clay); margin-top: 6px; font-family: 'JetBrains Mono', monospace; }
        @media (max-width: 720px) { .cf-r2, .cf-r3 { grid-template-columns: 1fr; } }
      `}</style>

      <div className="cf-row cf-r2">
        <div className="cf-field">
          <label style={lbl}>01 / Your name</label>
          <input
            style={inp}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="First and last"
          />
        </div>
        <div className="cf-field">
          <label style={lbl}>02 / Email</label>
          <input
            type="email"
            style={inp}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder="hello@yourdomain.com"
          />
          {emailInvalid ? <div className="cf-err">Please use a valid email address.</div> : null}
        </div>
      </div>

      <div className="cf-row cf-r2">
        <div className="cf-field">
          <label style={lbl}>
            03 / Telephone{" "}
            <span style={{ textTransform: "none", color: "var(--mute)", letterSpacing: 0 }}>
              (optional)
            </span>
          </label>
          <input
            style={inp}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+33 6 …"
          />
        </div>
        <div className="cf-field">
          <label style={lbl}>04 / Project type</label>
          <select style={sel} value={proj} onChange={(e) => setProj(e.target.value)}>
            <option>Kitchen</option>
            <option>Bathroom</option>
            <option>Living room</option>
            <option>Full home</option>
            <option>Outdoor / terrace</option>
            <option>Single trade (paint, tile, …)</option>
            <option>Not sure yet</option>
          </select>
        </div>
      </div>

      <div className="cf-row cf-r2">
        <div className="cf-field">
          <label style={lbl}>05 / Indicative budget</label>
          <select style={sel} value={budget} onChange={(e) => setBudget(e.target.value)}>
            <option>Under 20k</option>
            <option>20k – 40k</option>
            <option>40k – 80k</option>
            <option>80k – 150k</option>
            <option>150k+</option>
            <option>Not sure yet</option>
          </select>
        </div>
        <div className="cf-field">
          <label style={lbl}>06 / Hoped start</label>
          <select style={sel} value={start} onChange={(e) => setStart(e.target.value)}>
            <option>As soon as possible</option>
            <option>Q3 2026</option>
            <option>Q4 2026</option>
            <option>Q1 2027</option>
            <option>Flexible</option>
          </select>
        </div>
      </div>

      <div className="cf-field" style={{ marginBottom: 36 }}>
        <label style={lbl}>07 / Tell us about the project</label>
        <textarea
          style={{ ...inp, resize: "vertical", minHeight: 120, padding: "14px 0" }}
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          placeholder="Address, surface, what you'd like to change, when you'd like to start — anything that gives us the brief."
        />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 22, flexWrap: "wrap" }}>
        <button
          type="submit"
          className="ed-btn"
          style={{
            background: canSend ? "var(--ink)" : "#9b9486",
            cursor: canSend ? "pointer" : "not-allowed",
          }}
          disabled={!canSend}>
          {sent ? "Sent — we'll reply within 24h ✓" : sending ? "Sending…" : "Send brief"}
          {!sent && !sending && <span className="arr">→</span>}
        </button>
        <div style={{ fontSize: 13, color: "var(--mute)" }}>
          We reply within 24h on weekdays. Your details stay with us.
        </div>
      </div>
    </form>
  );
};

const ContactInfo = () => (
  <section className="ed-section">
    <style>{`
      .ci-grid { display: grid; grid-template-columns: 1fr 1.2fr; gap: 80px; align-items: start; }
      .ci-left { padding-top: 16px; position: sticky; top: 120px; }
      .ci-block { padding: 28px 0; border-top: 1px solid var(--line); }
      .ci-block:first-of-type { border-top: 0; padding-top: 0; }
      .ci-block .lbl { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--mute); letter-spacing: 0.18em; text-transform: uppercase; margin-bottom: 14px; }
      .ci-block .v { font-family: 'Fraunces', serif; font-weight: 400; font-size: 24px; letter-spacing: -0.01em; line-height: 1.35; }
      .ci-block .v em { font-style: italic; color: var(--clay); }
      .ci-block .v.lg { font-size: 36px; }
      .ci-block .s { font-size: 14px; color: var(--ink-2); margin-top: 8px; line-height: 1.55; }
      .ci-block .pulse { display: inline-flex; align-items: center; gap: 8px; font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--green); letter-spacing: 0.14em; text-transform: uppercase; margin-top: 12px; }
      .ci-block .pulse::before { content: ''; width: 7px; height: 7px; border-radius: 50%; background: var(--green); }
      @media (max-width: 960px) { .ci-grid { grid-template-columns: 1fr; gap: 36px; } .ci-left { position: static; } }
    `}</style>
    <div className="ci-grid">
      <aside className="ci-left">
        <div className="ci-block">
          <div className="lbl">— Write to us</div>
          <div className="v">Tell us about your <em>project</em>.</div>
          <div className="s">A short form on the right. The more you give us, the more useful our reply.</div>
        </div>
        <div className="ci-block">
          <div className="lbl">— Or call</div>
          <div className="v lg">+33 6 18 24 32 11</div>
          <div className="s">Monday to Friday, 9:00 — 18:00 (CET)</div>
        </div>
        <div className="ci-block">
          <div className="lbl">— Email</div>
          <div className="v">info@citibatiment.com</div>
          <div className="s">Project briefs, press, partnerships.</div>
        </div>
        <div className="ci-block">
          <div className="lbl">— Visit the studio</div>
          <div className="v" style={{ lineHeight: 1.5 }}>
            12 rue d'Antibes<br />06400 Cannes, France
          </div>
          <div className="s">By appointment. We'd rather come to you.</div>
        </div>
      </aside>
      <div>
        <SectionHead
          num="01"
          eyebrow="Send a brief"
          title='Seven fields, <em>seven minutes</em>.'
          right=""
        />
        <ContactForm />
      </div>
    </div>
  </section>
);

const ContactMap = () => (
  <section style={{ padding: "64px 96px", borderTop: "1px solid var(--line)" }}>
    <style>{`
      .cm-wrap { position: relative; aspect-ratio: 16 / 7; background: var(--paper); border-radius: 6px; overflow: hidden; }
      .cm-bg { position: absolute; inset: 0;
        background:
          radial-gradient(circle at 60% 50%, rgba(176,90,60,0.10), transparent 40%),
          linear-gradient(180deg, #F4EFE6 0%, #E9E0D0 100%);
      }
      .cm-grid { position: absolute; inset: 0; opacity: 0.35; background-image:
        linear-gradient(rgba(27,31,35,0.08) 1px, transparent 1px),
        linear-gradient(90deg, rgba(27,31,35,0.08) 1px, transparent 1px);
        background-size: 64px 64px;
      }
      .cm-road { position: absolute; height: 2px; background: rgba(27,31,35,0.18); }
      .cm-pin { position: absolute; top: 50%; left: 60%; transform: translate(-50%, -50%); display: flex; flex-direction: column; align-items: center; }
      .cm-pin .label { background: var(--ink); color: var(--paper); padding: 10px 14px; border-radius: 4px; font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase; margin-bottom: 12px; white-space: nowrap; }
      .cm-pin .label strong { display: block; font-family: 'Fraunces', serif; font-weight: 400; font-size: 18px; letter-spacing: -0.01em; text-transform: none; margin-bottom: 2px; }
      .cm-pin .dot { width: 18px; height: 18px; border-radius: 50%; background: var(--clay); border: 4px solid var(--paper); box-shadow: 0 0 0 2px var(--clay); position: relative; }
      .cm-pin .dot::after { content: ''; position: absolute; inset: -8px; border-radius: 50%; border: 1px solid var(--clay); opacity: 0.4; animation: pulseR 2.2s ease-out infinite; }
      .cm-towns .t { position: absolute; font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--mute); letter-spacing: 0.12em; text-transform: uppercase; white-space: nowrap; }
      .cm-towns .t::before { content: ''; display: inline-block; width: 6px; height: 6px; background: var(--ink); border-radius: 50%; margin-right: 8px; vertical-align: middle; opacity: 0.5; }
      .cm-meta { position: absolute; bottom: 24px; left: 28px; font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--mute); letter-spacing: 0.14em; text-transform: uppercase; }
      .cm-meta strong { color: var(--ink); font-weight: 500; }
      .cm-zoom { position: absolute; top: 24px; right: 24px; display: flex; gap: 4px; }
      .cm-zoom button { width: 36px; height: 36px; background: var(--paper); border: 1px solid var(--line); border-radius: 4px; font-size: 18px; cursor: pointer; }
    `}</style>
    <div className="cm-wrap">
      <div className="cm-bg"></div>
      <div className="cm-grid"></div>
      <div className="cm-road" style={{ top: "40%", left: "5%", right: "5%" }}></div>
      <div className="cm-road" style={{ top: "60%", left: "20%", right: "5%", transform: "rotate(-2deg)" }}></div>
      <div className="cm-road" style={{ top: "70%", left: "10%", right: "40%", transform: "rotate(3deg)" }}></div>
      <div className="cm-towns" style={{ position: "absolute", inset: 0 }}>
        <span className="t" style={{ top: "22%", left: "18%" }}>Mougins</span>
        <span className="t" style={{ top: "34%", left: "78%" }}>Antibes</span>
        <span className="t" style={{ top: "68%", left: "12%" }}>Le Cannet</span>
        <span className="t" style={{ top: "78%", left: "40%" }}>Croisette</span>
        <span className="t" style={{ top: "46%", left: "88%" }}>Cap d'Antibes</span>
        <span className="t" style={{ top: "20%", left: "52%" }}>Vallauris</span>
      </div>
      <div className="cm-pin">
        <div className="label">
          <strong>CitiBatiment Studio</strong>
          12 rue d'Antibes · Cannes
        </div>
        <div className="dot"></div>
      </div>
      <div className="cm-zoom">
        <button type="button">−</button>
        <button type="button">+</button>
      </div>
      <div className="cm-meta">
        43.5528° N · 7.0174° E · <strong>Open today 9:00 — 18:00</strong>
      </div>
    </div>
  </section>
);

const ContactFAQ = () => {
  const [open, setOpen] = useState(0);
  const faqs = [
    { q: "How quickly can you start?", a: "For a single-trade job (paint, tile, plumbing), usually within 3–5 weeks. For a full kitchen or bathroom, we're currently booking Q3 2026 starts. We cap the studio at nine active builds." },
    { q: "Do you give estimates over the phone?", a: "Not realistic ones. The first conversation is free — usually 30–45 minutes. After that we visit the space (or you send a floor plan + photos), and you get a written estimate within five working days." },
    { q: "Do you work outside the Côte d'Azur?", a: "Rarely. Site time is the only variable that shows in the finish, and we don't travel further than a 40-minute drive from Cannes for the active build." },
    { q: "What's included in the price?", a: "Everything: project lead, drawings, contractors, materials, finish, cleaning. The only thing you pay separately is permits — and we apply on your behalf." },
    { q: "What's your warranty?", a: "Ten years on every structural and trade element, two years on finishes. We come back at month six and year one to check and adjust at no cost." },
  ];
  return (
    <section className="ed-section" style={{ background: "var(--paper)" }}>
      <style>{`
        .fq-grid { display: grid; grid-template-columns: 1fr 1.4fr; gap: 80px; align-items: start; }
        .fq-list { border-top: 1px solid var(--line); }
        .fq-item { border-bottom: 1px solid var(--line); padding: 24px 0; cursor: pointer; }
        .fq-q { display: flex; align-items: center; justify-content: space-between; gap: 20px; }
        .fq-q h3 { font-family: 'Fraunces', serif; font-weight: 400; font-size: 24px; letter-spacing: -0.01em; margin: 0; }
        .fq-q .ico { width: 26px; height: 26px; border-radius: 50%; border: 1px solid var(--ink); display: flex; align-items: center; justify-content: center; font-size: 16px; transition: transform .25s, background .25s; flex-shrink: 0; }
        .fq-item.on .fq-q .ico { background: var(--ink); color: var(--paper); transform: rotate(45deg); }
        .fq-a { font-size: 15px; line-height: 1.6; color: var(--ink-2); padding-right: 60px; max-height: 0; overflow: hidden; transition: max-height .35s ease, padding-top .35s ease; }
        .fq-item.on .fq-a { max-height: 240px; padding-top: 18px; }
        @media (max-width: 960px) { .fq-grid { grid-template-columns: 1fr; gap: 36px; } }
      `}</style>
      <div className="fq-grid">
        <div>
          <SectionHead
            num="02"
            eyebrow="Common questions"
            title='Before you <em>write</em>.'
            right=""
          />
          <p style={{ fontSize: 15, lineHeight: 1.6, color: "var(--ink-2)", margin: 0, maxWidth: 380 }}>
            The five questions we get most often. If yours isn't here, it goes at the top of the form.
          </p>
        </div>
        <div className="fq-list">
          {faqs.map((f, i) => (
            <div
              key={f.q}
              className={`fq-item ${open === i ? "on" : ""}`}
              onClick={() => setOpen(open === i ? -1 : i)}>
              <div className="fq-q">
                <h3>{f.q}</h3>
                <span className="ico">+</span>
              </div>
              <div className="fq-a">{f.a}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

function ContactPage() {
  return (
    <main className="page-enter">
      <PageHero
        eyebrow="Contact · 06 of 06"
        title={<>Let's begin the <em>conversation</em><span style={{ color: "var(--clay)" }}>.</span></>}
        lede="The first call is free, lasts 30–45 minutes, and is mostly us listening. By the end of it you'll know whether we're the right studio for your project — even if the answer is no."
        meta={[
          <><strong>24h</strong> Average reply</>,
          <><strong>9:00 — 18:00</strong> Mon — Fri</>,
          <><strong>Cannes, FR</strong> 43.5528° N</>,
        ]}
      />
      <ContactInfo />
      <ContactMap />
      <ContactFAQ />
      <Marquee items={["Write a brief", "Or call us", "Or visit Cannes", "One studio, one project lead", "From concept to keys"]} />
    </main>
  );
}

export default ContactPage;
